import { CubejsApi } from '@cubejs-client/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { security } from '../../../../app/security';
import { initCubejsApi } from '../../impl/cube/initCube';

/**
 * Hook to initialize the Cube.js instance.
 * Returns the CubejsApi instance once ready.
 */
export const useInitCubeJs = (isAuthenticated: boolean): CubejsApi | null => {
  const [cubejsApi, setCubejsApi] = useState<CubejsApi | null>(null);
  const [triggerCubeReload, setTriggerCubeReload] = useState<boolean>(false);
  const cubeCheckIntervalTimerRef = useRef<NodeJS.Timeout | null>(null);

  const initCubejs = useCallback(async () => {
    const accessToken = await security.getAccessTokenSilently()();
    setCubejsApi(initCubejsApi(accessToken));
  }, []);

  useEffect(() => {
    if (!cubejsApi && isAuthenticated) {
      console.log('Initializing CubeJS instance...');
      initCubejs();
    }
  }, [cubejsApi, initCubejs, isAuthenticated, triggerCubeReload]);

  useEffect(() => {
    if (cubejsApi === null && !cubeCheckIntervalTimerRef.current) {
      cubeCheckIntervalTimerRef.current = setInterval(() => {
        console.log('Cube is not ready yet, trying again...');
        setTriggerCubeReload((prev) => !prev);
      }, 3000);
    } else if (cubejsApi) {
      if (cubeCheckIntervalTimerRef.current) {
        console.log('Cube is ready, clearing cube backup reset timer...');
        clearInterval(cubeCheckIntervalTimerRef.current);
        cubeCheckIntervalTimerRef.current = null;
      }
    }
    return () => {
      if (cubeCheckIntervalTimerRef.current) {
        clearInterval(cubeCheckIntervalTimerRef.current);
      }
    };
  }, [cubejsApi]);

  return cubejsApi;
};
